"use client";

import Image from "next/image";

import { CircleChevronRight } from "lucide-react";

import aadvantagebanner from "@repo/ui/assets/images/aadvantage.png";
import cardbanner from "@repo/ui/assets/images/card-banner.jpg";
import jetsmartbanner from "@repo/ui/assets/images/jetsmart-banner.webp";
import searchbanner from "@repo/ui/assets/images/search-banner.jpg";
import { Button } from "@repo/ui/components/ui/button";
import { usePageContext } from "@repo/ui/contexts/PageContext";
import { Link } from "@repo/ui/navigation";
import type { HomePageContent } from "@repo/ui/types/cms/home";

import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";

const normalizeUrl = (url?: string) => {
  if (!url) return null;

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return { href: url, isExternal: true };
  }

  if (url.startsWith("www.")) {
    return { href: `https://${url}`, isExternal: true };
  }

  return { href: url, isExternal: false };
};

export default function Banner() {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();
  const { bannerCta, backgroundImageCards } = normalizedSections;

  const normalized = normalizeUrl(backgroundImageCards?.cards?.[0]?.button?.url);
  const normalizedCard = normalizeUrl(backgroundImageCards?.cards?.[1]?.button?.url);

  return (
    <div
      className="flex flex-col items-center w-full space-y-6 responsive-padding responsive-layout"
      data-testid="homepage-banner"
    >
      {bannerCta && (
        <div className="relative w-full p-20 overflow-hidden text-white transition-transform duration-300 ease-out bg-blue-500 rounded-xl ">
          <div className="absolute inset-0 z-0">
            <Image
              src={bannerCta.backgroundImageUrl || jetsmartbanner}
              alt="JetSMART and American Airlines planes"
              fill
              quality={70}
              sizes="(min-width: 1200px) 1024px, calc(85.68vw + 13px)"
              className="lg:object-cover object-center opacity-80 hidden md:block"
            />
            <Image
              src={bannerCta.mobileBackgroundImageUrl || aadvantagebanner}
              alt="JetSMART and American Airlines planes"
              fill
              quality={70}
              sizes="(max-width: 768px) 100vw"
              className="object-cover object-center opacity-80 md:hidden"
            />
          </div>
        </div>
      )}
      <div className="flex flex-col w-full gap-6 md:flex-row">
        <div className="relative flex-1 overflow-hidden transition-transform duration-300 ease-out bg-white shadow-lg rounded-xl ">
          <Image
            src={backgroundImageCards?.cards?.[0]?.imageUrl ?? searchbanner}
            alt="Search SMART"
            fill
            quality={70}
            sizes="(max-width: 768px) 100vw, 50vw"
            className="object-cover opacity-80"
          />
          <div className="relative p-6 text-white">
            <h2 className="text-lg"> {backgroundImageCards?.cards?.[0]?.title}</h2>
            <h1 className="text-2xl font-bold text-white">
              {backgroundImageCards?.cards?.[0]?.subtitle}
            </h1>
            {normalized?.isExternal ? (
              <a href={normalized.href} target="_blank" rel="noopener noreferrer">
                <Button className="relative top-[2px] mt-4 rounded-full bg-white text-brand-cyan transition hover:bg-gray-100">
                  {backgroundImageCards?.cards?.[0]?.button.label}
                  <CircleChevronRight className="relative top-[1px] h-6 w-6" />
                </Button>
              </a>
            ) : (
              <Link href={normalized?.href ?? "#"}>
                <Button className="relative top-[2px] mt-4 rounded-full bg-white text-brand-cyan transition hover:bg-gray-100">
                  {backgroundImageCards?.cards?.[0]?.button.label}
                  <CircleChevronRight className="relative top-[1px] h-6 w-6" />
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden transition-transform duration-300 ease-out bg-white shadow-lg rounded-xl ">
          <Image
            src={backgroundImageCards?.cards?.[1]?.imageUrl ?? cardbanner}
            alt="SMART Cards"
            fill
            quality={70}
            sizes="(max-width: 768px) 100vw, 50vw"
            className="object-cover opacity-80"
          />
          <div className="relative p-6 text-white">
            <h2 className="text-lg">{backgroundImageCards?.cards?.[1]?.title}</h2>
            <h1 className="text-2xl font-bold text-white">
              {backgroundImageCards?.cards?.[1]?.subtitle}
            </h1>
            {normalizedCard ? (
              normalizedCard.isExternal ? (
                <a href={normalizedCard.href} target="_blank" rel="noopener noreferrer">
                  <Button className="relative top-[2px] mt-4 rounded-full bg-white p-2 text-orange-400 transition hover:bg-gray-100">
                    {backgroundImageCards?.cards?.[1]?.button.label}
                    <CircleChevronRight className="relative top-[1px] h-6 w-6" />
                  </Button>
                </a>
              ) : (
                <Link href={normalizedCard.href}>
                  <Button className="relative top-[2px] mt-4 rounded-full bg-white p-2 text-orange-400 transition hover:bg-gray-100">
                    {backgroundImageCards?.cards?.[1]?.button.label}
                    <CircleChevronRight className="relative top-[1px] h-6 w-6" />
                  </Button>
                </Link>
              )
            ) : (
              <Button
                disabled
                className="relative top-[2px] mt-4 rounded-full bg-white p-2 text-orange-400 opacity-50"
              >
                {backgroundImageCards?.cards?.[1]?.button.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
